import React, { useState, useEffect, createContext, useContext } from "react";
import Sidebar, { MobileMenuButton } from "./Sidebar";
import TopHeader from "./TopHeader";
import BottomNav from "./BottomNav";
import NotificationToaster from "../NotificationToaster";
import ConnectivityBanner from "../ConnectivityBanner";
import PWAInstallBanner from "../PWAInstallBanner";
import OfflineUnavailable from "../OfflineUnavailable";
import RouteErrorBoundary from "../RouteErrorBoundary";
import useVisibility from "@/hooks/useVisibility";
import usePrivacyBlur from "@/hooks/usePrivacyBlur";
import useIOSKeyboardScroll from "@/hooks/useIOSKeyboardScroll";
import useOnline from "@/hooks/useOnline";
import { queryClient, invalidateByPrefix } from "@/lib/queryClient";
import { initWebVitals } from "@/lib/webVitals";
import { isPathOfflineAvailable } from "@/lib/offline-routes";
import { Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUnreadChat } from "@/contexts/UnreadChatContext";
import api from "@/lib/api";

// Create context for sidebar state
export const SidebarContext = createContext({ collapsed: false, setCollapsed: () => {} });

export function useSidebar() {
  return useContext(SidebarContext);
}

/** Renders the page from React Router unless the device is offline AND the
 *  current route has no cached read path. Lives in a child component so
 *  flipping `online` doesn't re-render the entire layout (header / sidebar).
 *
 *  Wraps Outlet in RouteErrorBoundary so a page crash shows an in-flow
 *  fallback instead of tearing down the entire shell. resetKey=pathname
 *  clears the error when the user navigates away via the sidebar. */
function OfflineRouteGuard() {
  const online = useOnline();
  const location = useLocation();
  if (!online && !isPathOfflineAvailable(location.pathname)) {
    return <OfflineUnavailable />;
  }
  return (
    <RouteErrorBoundary resetKey={location.pathname}>
      <Outlet />
    </RouteErrorBoundary>
  );
}

export default function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const { user } = useAuth();
  const { unreadTripIds } = useUnreadChat();
  const location = useLocation();

  // Fetch alert count once for badge in BottomNav
  useEffect(() => {
    if (!user || user.role === "super_admin") return;
    api
      .get("/alerts/unread-count")
      .then((r) => setAlertCount(r.data?.count || 0))
      .catch(() => {});
  }, [user]);

  // AlertsPage hace mark-all-read en background al abrirse; reseteamos el badge
  // local de forma optimista para que el punto rojo desaparezca al instante.
  useEffect(() => {
    if (location.pathname === "/alerts") setAlertCount(0);
  }, [location.pathname]);

  // Mensajes ahora viven dentro del tab de Alertas — el badge suma ambos.
  const totalAlertBadge = alertCount + unreadTripIds.size;

  // App lifecycle: cuando volvés a foreground después de >30s en background,
  // refetcheá las queries volátiles. Evita pintar datos stale tras minimizar
  // la PWA / cambiar de tab.
  useIOSKeyboardScroll();

  // Web vitals — captura LCP/CLS/INP/TTFB/FCP y reporta al backend para
  // analytics; visibles en /superadmin/web-vitals.
  useEffect(() => {
    if (user) initWebVitals(user.role);
  }, [user]);

  // Privacy blur — al perder foco la ventana (cambiar de tab, lock screen),
  // blureamos el contenido para que datos sensibles (pasajeros, expenses,
  // AOG) no queden expuestos si dejás el celular en una mesa.
  // Toggleable: persistido en user.settings.privacy_blur (default: on).
  // Se desactiva automáticamente en localhost para no estorbar al tomar
  // screenshots o debuggear la UI durante desarrollo.
  const isLocalhost =
    typeof window !== "undefined" &&
    /^(localhost|127\.0\.0\.1|::1)$/.test(window.location.hostname);
  const privacyBlurEnabled = !isLocalhost && user?.settings?.privacy_blur !== false;
  const windowFocused = usePrivacyBlur();
  const blurClass =
    privacyBlurEnabled && !windowFocused
      ? "blur-md transition-[filter] duration-150"
      : "transition-[filter] duration-150";

  useVisibility(
    (elapsed) => {
      if (elapsed > 30000) {
        invalidateByPrefix("/dashboard");
        invalidateByPrefix("/readiness");
        invalidateByPrefix("/calendar");
        queryClient.invalidateQueries({ queryKey: ["/alerts"], exact: true });
        queryClient.invalidateQueries({ queryKey: ["/trips/action-required"], exact: true });
        api
          .get("/alerts/unread-count")
          .then((r) => setAlertCount(r.data?.count || 0))
          .catch(() => {});
      }
    },
    { minHiddenMs: 1000 }
  );

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed }}>
      <div className="min-h-screen bg-[#121212] grid-bg">
        <Sidebar
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
        />
        {/* El privacy blur vive en un wrapper full-width (sin el margin del
            sidebar) a propósito: cualquier `filter` distinto de none convierte
            al elemento en el containing block de sus descendientes
            `position: fixed`. Si el filter cae sobre el div con `lg:ml-[240px]`,
            las páginas fixed (CBP es la única full-screen fixed) se re-anclan a
            ese origen desplazado y saltan ~240px al blurear. Manteniendo el
            filter en un wrapper sin margin, el containing block coincide con el
            viewport y el mapa no se mueve. El offset del sidebar baja al div
            interno, que nunca lleva filter. */}
        <div className={blurClass}>
          <div
            className={`min-h-screen flex flex-col transition-all duration-300 ${collapsed ? "lg:ml-[68px]" : "lg:ml-[240px]"}`}
          >
            {/* Tablet hamburger — hidden on xs (bottom nav handles it) and lg+ (sidebar always visible) */}
            <TopHeader
              mobileMenuButton={<MobileMenuButton onClick={() => setMobileOpen(true)} />}
            />
            <main className="flex-1 pt-3 sm:p-4 lg:p-6 max-w-[1920px] altiv-mobile-bottom-pb lg:pb-6 overflow-x-hidden">
              <OfflineRouteGuard />
            </main>
          </div>
        </div>
        {/* Bottom navigation — mobile only */}
        <BottomNav alertCount={totalAlertBadge} />
      </div>
      {/* In-app push toasts — anywhere in the app, even if user has app in foreground */}
      <NotificationToaster />
      <ConnectivityBanner />
      <PWAInstallBanner />
    </SidebarContext.Provider>
  );
}
